'use client';

import { useEffect, useState, useCallback } from 'react';
import { getEntriesWithFilter } from '@/lib/contentstack-sdk';
import { IHousePlanHit } from '@/lib/search/types';

// Types
type EnrichedCMSData = any;

interface UseModelHomesResult {
  modelHomes: IHousePlanHit[];
  loading: boolean;
  refetch: () => void;
  getModelHomeById: (
    studioId: number,
    hpId: number,
  ) => IHousePlanHit | undefined;
  getStudioModelHomes: (studioId: number) => IHousePlanHit[];
}

// In memory caches - note react cache is preferred for SSR
let baseModelHomeCache: IHousePlanHit[] | null = null;
let cmsModelHomeCache: EnrichedCMSData[] | null = null;

// Key generator for CMS merge
const createCMSKey = (
  studioId: string | number,
  elevationId: string | number,
) => `${studioId}|${elevationId}`;

// Fetch base model homes from Algolia (cached)
async function fetchModelHomesBase(): Promise<IHousePlanHit[]> {
  if (baseModelHomeCache) return baseModelHomeCache;

  const res = await fetch('/algolia/model-homes');

  if (!res.ok) throw new Error(`Failed to fetch model homes`);

  const data = await res.json();
  baseModelHomeCache = data;
  return data;
}

// Fetch all CMS enrichments (cached)
async function fetchAllCMSModelHomeData(): Promise<EnrichedCMSData[]> {
  if (cmsModelHomeCache) return cmsModelHomeCache;

  try {
    const result = await getEntriesWithFilter({
      contentTypeUid: 'tm_model_home_detail',
      filters: {},
    });

    const data = Array.isArray(result) ? result : [];
    cmsModelHomeCache = data;
    return data;
  } catch (err) {
    console.error('Error fetching CMS model homes:', err);
    return [];
  }
}

// Merge base + CMS
function mergeModelHomeData(
  baseData: IHousePlanHit[],
  cmsData: EnrichedCMSData[],
): IHousePlanHit[] {
  const cmsMap = new Map<string, EnrichedCMSData>();

  for (const entry of cmsData) {
    const studioId = entry?.studio_id?.location;
    const elevationId = entry?.model_home_id?.model_id;
    if (studioId && elevationId) {
      cmsMap.set(createCMSKey(studioId, elevationId), entry);
    }
  }

  return baseData.map((home) => {
    const floorPlan = home?.FloorPlans?.[0];
    if (!floorPlan) return home;

    const key = createCMSKey(floorPlan.StudioId, floorPlan.ElevationId);
    const cms = cmsMap.get(key) ?? null;

    return {
      ...home,
      cms,
    };
  });
}

// Main hook
export const useModelHomes = (): UseModelHomesResult => {
  const [modelHomes, setModelHomes] = useState<IHousePlanHit[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchAndEnrich = useCallback(async () => {
    setLoading(true);
    try {
      const [baseHomes, cmsData] = await Promise.all([
        fetchModelHomesBase(),
        fetchAllCMSModelHomeData(),
      ]);

      const merged = mergeModelHomeData(baseHomes, cmsData);
      setModelHomes(merged);
    } catch (err) {
      console.error('Failed to fetch and enrich model homes:', err);
      setModelHomes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAndEnrich();
  }, [fetchAndEnrich]);

  const getStudioModelHomes = useCallback(
    (studioId: number) =>
      modelHomes.filter((home) =>
        home?.FloorPlans?.some((fp) => fp.StudioId === studioId),
      ),
    [modelHomes],
  );

  const getModelHomeById = useCallback(
    (studioId: number, hpId: number) =>
      modelHomes.find((home) =>
        home?.FloorPlans?.some(
          (fp) => fp.StudioId === studioId && fp.ElevationId === hpId,
        ),
      ),
    [modelHomes],
  );

  return {
    modelHomes,
    loading,
    refetch: fetchAndEnrich,
    getModelHomeById,
    getStudioModelHomes,
  };
};
